import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    CMS,
    withPageContext,
    TrackingUtils,
    HotelRoomUtils,
    CurrentAccommodationSearchSessionStorage
} from '../../utils';
import ConnectedDpPaxSelector from './DpPaxSelector/DpPaxSelector';
import { getRooms, isHotelProductActive, isPrime, canAddRoom } from '../../redux/currentSearch/selectors';
import { addRoom } from '../../redux/currentSearch/actions';
import { DpPaxRestrictionPropsDefault, NonPrimeHotelPropsDefault, RoomsProps } from '../../models';
import { getAddRoomSelectorClassNames } from './useRoomSelectorClassNames';
import { ButtonDefault } from '../../components';
import buttonClassNames from '../SearchButton/buttonClassNames';
import { ErrorMessage } from '../PaxSelector';

export const RoomSelectorContent = ({ value, showAddRoom, addRoom, trackEvent, hasError, isNative, isPrime, isHotel, onClose }) => {
    const { t } = useTranslation();
    const isNonPrimeHotel = isHotel && !isPrime;
    const roomRestrictions = isNonPrimeHotel ? NonPrimeHotelPropsDefault : DpPaxRestrictionPropsDefault;
    const onClickAddRoom = () => {
        const newRoom = HotelRoomUtils.getNewRoom(value, roomRestrictions);
        addRoom(newRoom);
        CurrentAccommodationSearchSessionStorage.setRooms(newRoom);
        const scenario = isHotel ? `_sce:${TrackingUtils.LABEL.userScenario()}` : '';
        trackEvent({
            action: isHotel ? TrackingUtils.ACTION.HOTELS : TrackingUtils.ACTION.DP,
            label: TrackingUtils.LABEL.roomAction({ action: 'add', scenario }),
        });
    };
    const numPax = HotelRoomUtils.getTotalPax(value);
    const kidsAgeRange = HotelRoomUtils.getKidsRange(roomRestrictions);
    const addRoomClass = getAddRoomSelectorClassNames(isNative);
    const showErrorMaxNumPax = numPax >= roomRestrictions.maxPassengers;

    return (
        <>
            { value.map((room, index) => (
                <ConnectedDpPaxSelector
                    room={room}
                    roomNumber={index}
                    key={room.id}
                    numPax={numPax}
                    isNative={isNative}
                    hasError={hasError}
                    isNonPrimeHotel={isNonPrimeHotel}
                    showRoomHeader
                    paxRestrictions={isNonPrimeHotel ? roomRestrictions : HotelRoomUtils.getRoomPaxRanges(room, roomRestrictions, numPax)}
                    kidsAgeRange={kidsAgeRange}
                />
            )) }
            {showAddRoom && (isPrime || !isHotel) && (
                <div className={addRoomClass.wrapper} onClick={onClickAddRoom} data-testid="add-room-selector">
                    <span className="odf-icon odf-icon-plus odf-icon-s odf-text-action odf-space-inner-xs" />
                    <span className="odf-link"><CMS id="roomSelector.button.addRoom" /></span>
                </div>
            )}
            { showErrorMaxNumPax && (
                <div className="odf-space-inner-top-s odf-space-inner-right-m odf-space-inner-left-m">
                    <ErrorMessage
                        messages={[{
                            key: 'guestSelector.guests.max',
                            values: { number: roomRestrictions.maxPassengers }
                        }]}
                    />
                </div>
            )}
            {!isNative && (
                <div className="odf-input-group">
                    <div className="odf-space-inner-top-m odf-space-inner-bottom-m">
                        <ButtonDefault
                            className={`odf-col-span12 ${buttonClassNames('primary', false, false)}`}
                            onClick={onClose}
                        >
                            {t('search.button.done')}
                        </ButtonDefault>
                    </div>
                </div>
            )}
        </>
    );
};

RoomSelectorContent.propTypes = {
    value: RoomsProps.isRequired,
    showAddRoom: PropTypes.bool.isRequired,
    addRoom: PropTypes.func.isRequired,
    trackEvent: PropTypes.func.isRequired,
    hasError: PropTypes.bool,
    isNative: PropTypes.bool,
    isPrime: PropTypes.bool.isRequired,
    isHotel: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
};

RoomSelectorContent.defaultProps = {
    hasError: false,
    isNative: false
};

const mapStateToPropsSelector = createStructuredSelector({
    value: getRooms,
    showAddRoom: canAddRoom,
    isPrime: isPrime,
    isHotel: isHotelProductActive,
});

export const ConnectedRoomSelectorContent = connect(mapStateToPropsSelector, {
    addRoom,
})(withPageContext(RoomSelectorContent));
